import { useCallback, useMemo, useState } from "react";
import { KeyedRecordField, RecordValue } from "../readmission-f2f-types";

export interface NotePayloadBase {
  expandedSections: Record<string, boolean>;
}

export interface UseNotePayload<P extends NotePayloadBase> {
  payload: P;
  update: (patch: Partial<P>) => void;
  updateKeyedField: <K extends KeyedRecordField<P>>(
    field: K,
    key: string,
    value: RecordValue<P[K]>
  ) => void;
  isExpanded: (key: string) => boolean;
  toggleSection: (key: string) => void;
  isAllExpanded: boolean;
  toggleAllSections: () => void;
}

/** Owns a note's payload and section expansion; every setter is stable and merges functionally. */
export const useNotePayload = <P extends NotePayloadBase>(
  createInitial: () => P,
  sectionKeys: string[]
): UseNotePayload<P> => {
  const [payload, setPayload] = useState<P>(createInitial);

  const update = useCallback((patch: Partial<P>) => {
    setPayload((prev) => ({ ...prev, ...patch }));
  }, []);

  const updateKeyedField = useCallback(
    <K extends KeyedRecordField<P>>(
      field: K,
      key: string,
      value: RecordValue<P[K]>
    ) => {
      setPayload((prev) => ({
        ...prev,
        [field]: { ...(prev[field] as Record<string, unknown>), [key]: value },
      }));
    },
    []
  );

  const isExpanded = useCallback(
    (key: string) => !!payload.expandedSections[key],
    [payload.expandedSections]
  );

  const toggleSection = useCallback((key: string) => {
    setPayload((prev) => ({
      ...prev,
      expandedSections: {
        ...prev.expandedSections,
        [key]: !prev.expandedSections[key],
      },
    }));
  }, []);

  const isAllExpanded = sectionKeys.every(
    (key) => !!payload.expandedSections[key]
  );

  const toggleAllSections = useCallback(
    () =>
      setPayload((prev) => ({
        ...prev,
        expandedSections: Object.fromEntries(
          sectionKeys.map((key) => [key, !isAllExpanded])
        ),
      })),
    [sectionKeys, isAllExpanded]
  );

  return useMemo(
    () => ({
      payload,
      update,
      updateKeyedField,
      isExpanded,
      toggleSection,
      isAllExpanded,
      toggleAllSections,
    }),
    [
      payload,
      update,
      updateKeyedField,
      isExpanded,
      toggleSection,
      isAllExpanded,
      toggleAllSections,
    ]
  );
};
